import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import { useNotification } from '../components/Notification';
import './AdminRefundCreate.css';

function AdminRefundCreate() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [amount, setAmount] = useState('');
  const [refundMethod, setRefundMethod] = useState('card');
  const [returnId, setReturnId] = useState('');
  const [reason, setReason] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [notes, setNotes] = useState('');

  // Gauti užsakymo duomenis
  const { data: order, isLoading } = useQuery({
    queryKey: ['admin-order', id],
    queryFn: async () => {
      const res = await apiClient.get(`/admin/orders/${id}`);
      return res.data;
    },
  });

  // Apskaičiuoti jau refunduotą sumą
  const totalRefunded = useMemo(() => {
    if (!order?.refunds) return 0;
    return order.refunds
      .filter((refund) => refund.status !== 'cancelled')
      .reduce((sum, refund) => sum + parseFloat(refund.amount || 0), 0);
  }, [order?.refunds]);

  // Maksimali refund suma
  const maxRefundAmount = useMemo(() => {
    if (!order) return 0;
    return parseFloat(order.total || 0) - totalRefunded;
  }, [order, totalRefunded]);

  // Sukurti refund
  const createRefundMutation = useMutation({
    mutationFn: async (data) => {
      return apiClient.post('/admin/refunds', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-order', id]);
      queryClient.invalidateQueries(['admin-orders']);
      notification.success('Refund sukurtas sėkmingai');
      navigate(`/admin/orders/${id}`);
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || 'Klaida kuriant refund');
    },
  });

  const handleAmountChange = (value) => {
    const numValue = parseFloat(value) || 0;
    const maxValue = maxRefundAmount;
    if (numValue <= maxValue) {
      setAmount(value);
    } else {
      setAmount(maxValue.toString());
      notification.info(`Maksimali refund suma: ${maxValue.toFixed(2)} €`);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const refundAmount = parseFloat(amount);
    if (!refundAmount || refundAmount <= 0) {
      notification.warning('Įveskite teisingą refund sumą');
      return;
    }

    if (refundAmount > maxRefundAmount) {
      notification.info(`Refund suma negali viršyti ${maxRefundAmount.toFixed(2)} €`);
      return;
    }

    if (!refundMethod) {
      notification.warning('Pasirinkite refund metodą');
      return;
    }

    const refundData = {
      order_id: parseInt(id),
      amount: refundAmount,
      refund_method: refundMethod,
    };

    if (returnId) {
      refundData.return_id = parseInt(returnId);
    }

    if (reason.trim()) {
      refundData.reason = reason.trim();
    }

    if (transactionId.trim()) {
      refundData.transaction_id = transactionId.trim();
    }

    if (notes.trim()) {
      refundData.notes = notes.trim();
    }

    createRefundMutation.mutate(refundData);
  };

  if (isLoading) {
    return <div className="admin-refund-create-page container">Kraunama...</div>;
  }

  if (!order) {
    return <div className="admin-refund-create-page container">Užsakymas nerastas</div>;
  }

  return (
    <div className="admin-refund-create-page container">
      <div className="refund-header">
        <h1>Sukurti refund</h1>
        <p className="order-info">
          Užsakymas: <strong>{order.order_number}</strong>
        </p>
      </div>

      <form onSubmit={handleSubmit} className="refund-form">
        {/* Užsakymo informacija */}
        <div className="form-section">
          <h2>Užsakymo informacija</h2>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Užsakymo suma:</span>
              <span className="info-value">{parseFloat(order.total).toFixed(2)} €</span>
            </div>
            <div className="info-item">
              <span className="info-label">Jau refunduota:</span>
              <span className="info-value">{totalRefunded.toFixed(2)} €</span>
            </div>
            <div className="info-item highlight">
              <span className="info-label">Galima refunduoti:</span>
              <span className="info-value">{maxRefundAmount.toFixed(2)} €</span>
            </div>
          </div>
        </div>

        {/* Refund suma */}
        <div className="form-section">
          <h2>Refund suma</h2>
          <div className="amount-input-group">
            <label>
              Suma (€)
              <input
                type="number"
                step="0.01"
                min="0.01"
                max={maxRefundAmount}
                value={amount}
                onChange={(e) => handleAmountChange(e.target.value)}
                className="amount-input"
                placeholder="0.00"
                required
              />
            </label>
            <div className="amount-helpers">
              <button
                type="button"
                onClick={() => setAmount((maxRefundAmount * 0.25).toFixed(2))}
                className="btn-helper"
              >
                25%
              </button>
              <button
                type="button"
                onClick={() => setAmount((maxRefundAmount * 0.5).toFixed(2))}
                className="btn-helper"
              >
                50%
              </button>
              <button
                type="button"
                onClick={() => setAmount((maxRefundAmount * 0.75).toFixed(2))}
                className="btn-helper"
              >
                75%
              </button>
              <button
                type="button"
                onClick={() => setAmount(maxRefundAmount.toFixed(2))}
                className="btn-helper"
              >
                100%
              </button>
            </div>
          </div>
          {amount && parseFloat(amount) > 0 && (
            <div className="amount-preview">
              Refunduojama: <strong>{parseFloat(amount).toFixed(2)} €</strong>
            </div>
          )}
        </div>

        {/* Refund metodas */}
        <div className="form-section">
          <h2>Refund metodas</h2>
          <div className="method-options">
            <label className="method-option">
              <input
                type="radio"
                name="refund_method"
                value="card"
                checked={refundMethod === 'card'}
                onChange={(e) => setRefundMethod(e.target.value)}
              />
              <div className="method-info">
                <strong>Mokėjimo kortele</strong>
                <span>Grąžinimas į tą pačią kortelę</span>
              </div>
            </label>
            <label className="method-option">
              <input
                type="radio"
                name="refund_method"
                value="bank_transfer"
                checked={refundMethod === 'bank_transfer'}
                onChange={(e) => setRefundMethod(e.target.value)}
              />
              <div className="method-info">
                <strong>Banko pavedimas</strong>
                <span>Grąžinimas banko sąskaitą</span>
              </div>
            </label>
            <label className="method-option">
              <input
                type="radio"
                name="refund_method"
                value="store_credit"
                checked={refundMethod === 'store_credit'}
                onChange={(e) => setRefundMethod(e.target.value)}
              />
              <div className="method-info">
                <strong>Parduotuvės kreditas</strong>
                <span>Pridėti kaip store credit</span>
              </div>
            </label>
            <label className="method-option">
              <input
                type="radio"
                name="refund_method"
                value="cash"
                checked={refundMethod === 'cash'}
                onChange={(e) => setRefundMethod(e.target.value)}
              />
              <div className="method-info">
                <strong>Grynieji</strong>
                <span>Grąžinimas grynaisiais</span>
              </div>
            </label>
          </div>
        </div>

        {/* RMA susiejimas */}
        {order.returns && order.returns.length > 0 && (
          <div className="form-section">
            <h2>Susieti su RMA (neprivaloma)</h2>
            <select
              value={returnId}
              onChange={(e) => setReturnId(e.target.value)}
              className="select-input"
            >
              <option value="">Nesusieti su RMA</option>
              {order.returns.map((returnItem) => (
                <option key={returnItem.id} value={returnItem.id}>
                  {returnItem.return_number} - {returnItem.status} -{' '}
                  {parseFloat(returnItem.refund_amount).toFixed(2)} €
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Priežastis */}
        <div className="form-section">
          <h2>Priežastis (neprivaloma)</h2>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Aprašykite refund priežastį..."
            className="reason-textarea"
            rows="3"
          />
        </div>

        {/* Transakcijos ID */}
        <div className="form-section">
          <h2>Transakcijos ID (neprivaloma)</h2>
          <input
            type="text"
            value={transactionId}
            onChange={(e) => setTransactionId(e.target.value)}
            placeholder="Įveskite transakcijos ID, jei yra..."
            className="text-input"
          />
        </div>

        {/* Pastabos */}
        <div className="form-section">
          <h2>Pastabos (neprivaloma)</h2>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Papildomos pastabos..."
            className="notes-textarea"
            rows="3"
          />
        </div>

        {/* Santrauka */}
        {amount && parseFloat(amount) > 0 && (
          <div className="form-section summary-section">
            <h2>Refund santrauka</h2>
            <div className="summary-box">
              <div className="summary-row">
                <span>Refund suma:</span>
                <span>{parseFloat(amount).toFixed(2)} €</span>
              </div>
              <div className="summary-row">
                <span>Metodas:</span>
                <span>
                  {refundMethod === 'card'
                    ? 'Mokėjimo kortele'
                    : refundMethod === 'bank_transfer'
                    ? 'Banko pavedimas'
                    : refundMethod === 'store_credit'
                    ? 'Parduotuvės kreditas'
                    : 'Grynieji'}
                </span>
              </div>
              {returnId && (
                <div className="summary-row">
                  <span>Susietas su RMA:</span>
                  <span>
                    {
                      order.returns?.find((r) => r.id === parseInt(returnId))
                        ?.return_number
                    }
                  </span>
                </div>
              )}
              {refundMethod === 'store_credit' && (
                <div className="summary-note">
                  <strong>Pastaba:</strong> Store credit bus automatiškai sukurtas
                  klientui po refund sukūrimo.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Mygtukai */}
        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate(`/admin/orders/${id}`)}
            className="btn btn-secondary"
          >
            Atšaukti
          </button>
          <button
            type="submit"
            disabled={createRefundMutation.isLoading || !amount || parseFloat(amount) <= 0}
            className="btn btn-primary"
          >
            {createRefundMutation.isLoading ? 'Kuriama...' : 'Sukurti refund'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AdminRefundCreate;
